import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import Logomark from "../components/Logomark";

const easeOut = [0.16, 1, 0.3, 1];

export default function Privacy() {
  const { t, lang, toggleLang } = useLang();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const p = t.privacy;

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-[#F4F4F5]">
      {/* Top bar */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0A0A0B]/85 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-8 lg:px-12 h-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="privacy-home">
            <Logomark size={36} />
            <div className="hidden sm:block leading-none text-left border-l border-white/10 pl-3">
              <div className="font-display font-black tracking-tight text-[15px] text-white">
                DEPP C&amp;M
              </div>
              <div className="text-[9px] tracking-[0.25em] uppercase text-[#8B939C] mt-1">
                S.A. DE C.V.
              </div>
            </div>
          </Link>

          <div className="flex items-center gap-3">
            <button
              onClick={toggleLang}
              data-testid="privacy-lang-toggle"
              className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#F4F4F5] border border-white/15 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
            >
              {lang.toUpperCase()}
            </button>
            <Link
              to="/"
              data-testid="privacy-back"
              className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-white hover:text-[#D4AF37] transition-colors duration-300"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {p.back}
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-[72px]">
        {/* Hero banner */}
        <section className="relative py-20 sm:py-28 border-b border-white/5 overflow-hidden">
          <div className="absolute inset-0 grid-overlay opacity-30 pointer-events-none" />
          <div className="relative max-w-[1400px] mx-auto px-6 sm:px-8 lg:px-12">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#D4AF37]" />
              <span className="overline">{p.eyebrow}</span>
            </div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: easeOut }}
              className="font-display font-black text-white text-[clamp(2.5rem,5vw,4.5rem)] tracking-tight leading-[1.02]"
            >
              {p.title}
            </motion.h1>
            <div className="mt-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.25em] text-[#8B939C]">
              <FileText className="w-3.5 h-3.5" />
              {p.updated}: 22 / 04 / 2026
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-24">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-8 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              {/* TOC */}
              <aside className="lg:col-span-4">
                <div className="lg:sticky lg:top-24">
                  <div className="overline mb-5">{p.toc}</div>
                  <nav>
                    <ol className="space-y-2 list-none">
                      {p.sections.map((s) => (
                        <li key={s.id}>
                          <a
                            href={`#${s.id}`}
                            data-testid={`toc-${s.id}`}
                            className="text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors flex gap-2"
                          >
                            <span className="text-[#D4AF37] font-mono text-xs mt-0.5">
                              {s.id === "contact" ? "—" : "·"}
                            </span>
                            <span>{s.heading}</span>
                          </a>
                        </li>
                      ))}
                    </ol>
                  </nav>
                </div>
              </aside>

              {/* Body */}
              <article className="lg:col-span-8 max-w-none">
                <div className="mb-10 pb-10 border-b border-white/5">
                  <div className="font-display font-bold text-white text-xl lg:text-2xl tracking-tight">
                    {p.intro_title}
                  </div>
                </div>

                <div className="space-y-14">
                  {p.sections.map((s) => (
                    <section key={s.id} id={s.id} data-testid={`privacy-section-${s.id}`}>
                      <h2 className="font-display font-bold text-white text-2xl lg:text-3xl tracking-tight leading-tight">
                        {s.heading}
                      </h2>
                      <div className="mt-5 space-y-4 text-[15px] leading-[1.75] text-[#A1A1AA]">
                        {s.body && <p>{s.body}</p>}
                        {s.list && (
                          <ul className="space-y-2 pl-4 border-l border-[#D4AF37]/30">
                            {s.list.map((li, i) => (
                              <li key={i} className="text-sm leading-relaxed text-[#A1A1AA]">
                                {li}
                              </li>
                            ))}
                          </ul>
                        )}
                        {s.body2 && <p>{s.body2}</p>}
                        {s.list2 && (
                          <ul className="space-y-2 pl-4 border-l border-[#D4AF37]/30">
                            {s.list2.map((li, i) => (
                              <li key={i} className="text-sm leading-relaxed text-[#A1A1AA]">
                                {li}
                              </li>
                            ))}
                          </ul>
                        )}
                        {s.body3 && <p>{s.body3}</p>}
                      </div>
                    </section>
                  ))}
                </div>

                <div className="mt-16 pt-10 border-t border-white/5">
                  <Link
                    to="/"
                    data-testid="privacy-accept"
                    className="inline-flex items-center gap-3 bg-[#D4AF37] text-black px-7 py-4 text-[12px] font-bold uppercase tracking-[0.25em] hover:bg-[#F0C84B] transition-colors duration-300"
                  >
                    {p.accept}
                    <ArrowLeft className="w-4 h-4 rotate-180" />
                  </Link>
                </div>
              </article>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/5 py-10">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8B939C]">
            © {new Date().getFullYear()} DEPP C&amp;M, S.A. DE C.V.
          </div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8B939C]">
            privacidad@deppcm.com
          </div>
        </div>
      </footer>
    </div>
  );
}
